import { meGetGamificationAccountTransactions } from '../../features/queries/meGetGamificationAccountTransactions';
import { meGetReports } from '../../features/queries/meGetReports';
import useVoteTokensPool from '../../hooks/useVoteTokensPool';
import { useQ } from '../../lib/query';
import { useGamification } from '../../providers/GamificationProvider';
import { useAuth } from '@presov.sk/presov-auth-react';
import { isSameMonth } from 'date-fns';
import React from 'react';

/**
 * Koľko transakcií stiahnuť na dopočítanie prírastku bodov za aktuálny mesiac.
 * Gamifikačné API neponúka agregát, takže sa počíta z poslednej strany.
 */
const TRANSACTIONS_SUMMARY_LIMIT = 100;

/** Rovnako aj podnety - `/me/reports` nevracia celkový počet, len stranu. */
const REPORTS_SUMMARY_LIMIT = 100;

/**
 * Čísla, ktoré profil ukazuje na **každej** svojej sekcii: identita, počet
 * podnetov, body a žetóny.
 *
 * Je to hook, nie komponent, lebo tie isté tri čísla potrebuje ľavý pás
 * (kachličky a menovky sekcií), hlavička stránky (tichý údaj vedľa titulu) aj
 * mobilná karta so súhrnom. Dopyty sú `useQ`, takže sa medzi nimi nezdvojujú -
 * react-query ich zlúči podľa kľúča.
 */
export const useProfileSummary = () => {
  const { authUser, authState } = useAuth();
  const isAuthorized = authState === 'authorized';

  const {
    generalAccountPoints,
    generalAccountId,
    gamificationTier,
    isLoadingPointAccounts,
    pointAccounts,
  } = useGamification();

  const { data: transactionsData, isLoading: isLoadingTransactions } = useQ(
    meGetGamificationAccountTransactions,
    { accountId: generalAccountId, limit: TRANSACTIONS_SUMMARY_LIMIT },
    { enabled: isAuthorized && !!generalAccountId },
  );

  const {
    available: availableTokens,
    total: totalTokens,
    isLoading: isLoadingTokens,
  } = useVoteTokensPool();

  const { data: reportsData, isLoading: isLoadingReports } = useQ(
    meGetReports,
    { limit: REPORTS_SUMMARY_LIMIT },
    { enabled: isAuthorized },
  );

  // Prírastok za aktuálny mesiac - jediné, čo kachlička Body potrebuje nad
  // rámec zostatku.
  const monthPoints = React.useMemo(() => {
    const now = new Date();

    return (transactionsData?.transactions || [])
      .filter((transaction) => transaction.status === 'confirmed')
      .filter((transaction) =>
        isSameMonth(new Date(transaction.created_at), now),
      )
      .reduce((sum, transaction) => sum + (transaction.points || 0), 0);
  }, [transactionsData]);

  const reports = reportsData?.reports || [];
  const reportsInProgress = reports.filter((report) => report.accepted).length;

  const fullName = [authUser?.name, authUser?.surname]
    .filter(Boolean)
    .join(' ');

  return {
    fullName,
    email: authUser?.email,
    tier: gamificationTier?.title,
    // Vek účtu berieme z gamifikačného účtu - v id tokene dátum registrácie nie je.
    memberSince: pointAccounts[0]?.created_at
      ? new Date(pointAccounts[0].created_at).getFullYear()
      : undefined,

    reportsCount: reports.length,
    // Bez agregátu vieme spoľahlivo len to, čo sa zmestilo na jednu stranu.
    reportsCountLabel: reportsData?.hasNextOffset
      ? `${reports.length}+`
      : `${reports.length}`,
    reportsInProgress,
    isLoadingReports,

    points: generalAccountPoints,
    monthPoints,
    isLoadingPoints: isLoadingPointAccounts || isLoadingTransactions,

    availableTokens,
    totalTokens,
    isLoadingTokens,
  };
};

export type ProfileSummary = ReturnType<typeof useProfileSummary>;
